import { Component, Inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCalendar } from '@angular/material/datepicker';
import { finalize, Subject, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import { helper } from 'src/environments/helper';
import { ICerere } from '../../../cereri.interface';
import { CereriService } from '../../../cereri.service';
import { AddProgramareService } from '../../../add-programare/add-programare.service';
import { NotificationService } from 'src/app/services/notification.service';

interface AvailableSlot {
    time: string;
    isCurrentSlot: boolean;
}

@Component({
    selector: 'app-reschedule-programare',
    templateUrl: './reschedule-programare.component.html',
    styleUrls: ['./reschedule-programare.component.scss']
})
export class RescheduleProgramareComponent implements OnInit, OnDestroy {
    @ViewChild('calendar') calendar!: MatCalendar<Date>;

    helper = helper;
    isLoading = false;
    loadingSlots = false;

    cerereData: ICerere | null = null;

    // Destroy subject for cleanup
    private destroy$ = new Subject<void>();

    // Calendar
    minDate: Date = new Date();
    maxDate: Date = new Date(new Date().setMonth(new Date().getMonth() + 3));
    fullDays: string[] = [];
    zileLibere: string[] = [];
    selectedDate: Date | null = null;

    // Sloturi disponibile
    availableSlots: AvailableSlot[] = [];
    selectedSlot: string | null = null;
    reservingSlot = false;

    // Pentru a ține slot-ul temporar rezervat
    tempAppointmentId: number | null = null;

    // Info programare curentă
    currentSlotDate: string = '';
    currentSlotTime: string = '';

    form: FormGroup = new FormGroup({
        date: new FormControl(null, [Validators.required]),
        time: new FormControl(null, [Validators.required]),
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { cerereData: ICerere },
        private dialogRef: MatDialogRef<RescheduleProgramareComponent>,
        private cereriService: CereriService,
        private addProgramareService: AddProgramareService,
        private notificationService: NotificationService,
    ) {
        this.cerereData = data.cerereData;
    }

    ngOnInit() {
        if (this.cerereData?.programare) {
            this.currentSlotDate = this.cerereData.programare.date;
            this.currentSlotTime = this.cerereData.programare.time_from;
            this.loadCalendarData();
        }
    }

    ngOnDestroy() {
        // Eliberează slotul temporar dacă dialogul e închis fără salvare
        if (this.tempAppointmentId) {
            this.addProgramareService.resetAppointment({ appointmentIds: [this.tempAppointmentId] })
                .subscribe();
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadCalendarData() {
        if (!this.cerereData?.programare) return;

        const p = this.cerereData.programare;
        this.isLoading = true;

        // Încarcă zilele libere
        this.addProgramareService.getZileLibere()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.zileLibere = (res?.data || []).map((z: any) => z.date);
                }
            });

        // Încarcă zilele pline
        this.addProgramareService.getFullDays(p.id_modul, p.id_locatie, p.id_serviciu, p.id_program_type)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (res: any) => {
                    const data = res?.data || {};
                    this.fullDays = data.fullDays || [];
                    if (data.minDate) {
                        this.minDate = new Date(data.minDate);
                    }
                    if (data.maxDate) {
                        this.maxDate = new Date(data.maxDate);
                    }
                    if (this.calendar) {
                        this.calendar.updateTodaysDate();
                    }
                }
            });
    }

    // Filter pentru calendar - dezactivează zilele pline și libere
    dateFilter = (date: Date | null): boolean => {
        if (!date) return false;

        const dateStr = this.formatDateForApi(date);
        const dayOfWeek = date.getDay();

        // Exclude weekends
        if (dayOfWeek === 0 || dayOfWeek === 6) return false;

        // Dezactivează zilele libere
        if (this.zileLibere.includes(dateStr)) return false;

        // Dezactivează zilele pline (DAR permite ziua programării curente pentru afișare slot curent)
        if (this.fullDays.includes(dateStr) && dateStr !== this.currentSlotDate) return false;

        return true;
    };

    onDateSelected(date: Date | null) {
        if (!date || !this.cerereData?.programare) return;

        this.selectedDate = date;
        this.form.patchValue({
            date: this.formatDateForApi(date),
            time: ''
        });
        this.availableSlots = [];
        this.selectedSlot = null;

        // Eliberează slotul temporar anterior dacă există
        if (this.tempAppointmentId) {
            this.addProgramareService.resetAppointment({ appointmentIds: [this.tempAppointmentId] })
                .pipe(takeUntil(this.destroy$))
                .subscribe();
            this.tempAppointmentId = null;
        }

        this.loadAvailableSlots(date);
    }

    loadAvailableSlots(date: Date) {
        if (!this.cerereData?.programare) return;

        this.loadingSlots = true;
        const p = this.cerereData.programare;
        const dateStr = this.formatDateForApi(date);

        const payload = {
            id_modul: p.id_modul,
            id_locatie: p.id_locatie,
            id_serviciu: p.id_serviciu,
            id_program_type: p.id_program_type,
            date: dateStr,
        };

        this.addProgramareService.getAvailableSlots(payload)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.loadingSlots = false)
            )
            .subscribe({
                next: (res: any) => {
                    // Response is {data: {hours: [...], max_sloturi: N}} or fallback to array
                    const responseData = res?.data || res;
                    let times: string[] = responseData?.hours || (Array.isArray(responseData) ? responseData : []);

                    // Dacă suntem pe aceeași dată, adaugă slotul curent în listă dacă nu e deja
                    if (dateStr === this.currentSlotDate && !times.includes(this.currentSlotTime)) {
                        times.push(this.currentSlotTime);
                        times.sort();
                    }

                    // Convertim în obiecte slot
                    this.availableSlots = times.map(time => ({
                        time: time,
                        isCurrentSlot: dateStr === this.currentSlotDate && time === this.currentSlotTime
                    }));
                },
                error: () => {
                    this.availableSlots = [];
                }
            });
    }

    onSlotSelect(slot: AvailableSlot) {
        if (!slot || !this.cerereData?.programare) return;

        // Nu permite selectarea slotului curent
        if (slot.isCurrentSlot) return;

        // Nu permite selectare în timpul rezervării
        if (this.reservingSlot) return;

        // Eliberează slotul temporar anterior
        if (this.tempAppointmentId) {
            this.addProgramareService.resetAppointment({ appointmentIds: [this.tempAppointmentId] })
                .pipe(takeUntil(this.destroy$))
                .subscribe();
            this.tempAppointmentId = null;
        }

        // Rezervă temporar noul slot
        const p = this.cerereData.programare;
        const payload = {
            id_modul: p.id_modul,
            id_locatie: p.id_locatie,
            id_serviciu: p.id_serviciu,
            id_program_type: p.id_program_type,
            date: this.form.value.date,
            time: slot.time,
        };

        // Setează selecția imediat pentru feedback vizual
        this.selectedSlot = slot.time;
        this.reservingSlot = true;

        this.addProgramareService.makeAppointment(payload)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.reservingSlot = false)
            )
            .subscribe({
                next: (res: any) => {
                    // Response is the appointment ID directly
                    this.tempAppointmentId = res?.data || res || null;
                    this.form.controls['time'].setValue(slot.time);
                },
                error: () => {
                    // Reset selecția dacă rezervarea a eșuat
                    this.selectedSlot = null;
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        'Slotul nu mai este disponibil.',
                        helper.dialogConfig.icons.error
                    );
                    // Reîncarcă sloturile
                    if (this.selectedDate) {
                        this.loadAvailableSlots(this.selectedDate);
                    }
                }
            });
    }

    formatDateForApi(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    closeDialog() {
        this.dialogRef.close({ success: false });
    }

    submitForm() {
        if (!this.tempAppointmentId) {
            this.notificationService.warningSwal(
                helper.dialogConfig.headers.errorAtentie,
                'Vă rugăm să selectați un slot disponibil.',
                helper.dialogConfig.icons.error
            );
            return;
        }

        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'Confirmați reprogramarea?',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.doReschedule();
            }
        });
    }

    doReschedule() {
        this.isLoading = true;

        this.cereriService
            .rescheduleAppointment(this.cerereData!.id, { new_appointment_id: this.tempAppointmentId! })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (res: any) => {
                    // Nu elibera slotul - a fost transferat
                    this.tempAppointmentId = null;

                    Swal.fire({
                        title: helper.dialogConfig.headers.success,
                        text: res?.data?.message || 'Programarea a fost reprogramată cu succes.',
                        icon: 'success',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        allowOutsideClick: false,
                    }).then(() => {
                        this.dialogRef.close({ success: true });
                    });
                },
                error: (err) => {
                    let msg = err?.error?.errors?.message?.[0] || err?.error?.message || helper.dialogConfig.generalMessages.error;
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        helper.dialogConfig.icons.error
                    );
                },
            });
    }
}
